<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import { ATTENDANCE_STATUS_LABELS, HR_EDIT_STATUS_OPTIONS } from '@/modules/backoffice/constants/attendance.const.ts'
import { useAttendanceReportStore } from '@/modules/backoffice/stores/attendance.store.ts'

const store = useAttendanceReportStore()
const dayDetail = computed(() => store.dayDetail)

const props = defineProps<{
  isVisible: boolean
  selectedEmp: any
  selectedDay: number | null
  month: number
  year: number
}>()

const emit = defineEmits<{
  (e: 'update:isVisible', value: boolean): void
  (e: 'save', payload: any): void
}>()

// HR Form State
const tempAmStatus = ref<string>('')
const tempPmStatus = ref<string>('')
const tempWorkDays = ref<number>(0)
const tempWorkHours = ref<number>(0)
const tempNote = ref<string>('')

// Reset form when modal opens with new employee/day
watch(() => props.isVisible, (newVal) => {
  if (newVal && props.selectedEmp && props.selectedDay) {
    const dayData = props.selectedEmp.attendance[props.selectedDay]
    tempAmStatus.value = dayData?.am || ''
    tempPmStatus.value = dayData?.pm || ''
    tempWorkDays.value = dayData?.work_day || 0
    tempWorkHours.value = dayData?.total_hours || 0
    tempNote.value = ''
  }
})

const closeModal = () => {
  emit('update:isVisible', false)
}

const saveAttendance = () => {
  emit('save', {
    am: tempAmStatus.value,
    pm: tempPmStatus.value,
    work_day: tempWorkDays.value,
    total_hours: tempWorkHours.value,
    note: tempNote.value
  })
  closeModal()
}

const getShiftDetail = (type: 'morning' | 'afternoon' | 'ot') => {
  const stats = dayDetail.value?.stats || {};
  if (type === 'morning') {
    return { in: stats.morning_start || '--:--', out: stats.morning_end || '--:--' };
  } else if (type === 'afternoon') {
    return { in: stats.afternoon_start || '--:--', out: stats.afternoon_end || '--:--' };
  } else if (type === 'ot') {
    return { in: stats.ot_start || '--:--', out: stats.ot_end || '--:--' };
  }
  return { in: '--:--', out: '--:--' };
}

const getOverallStatusClass = (status: string | null) => {
  if (!status) return 'text-slate-400 bg-slate-100'
  if (['ĐỦ CÔNG', 'ĐỦ CÔNG CÓ TĂNG CA'].includes(status)) return 'text-emerald-700 bg-emerald-100 border-emerald-200'
  if (status === 'THIẾU CÔNG') return 'text-amber-700 bg-amber-100 border-amber-200'
  if (status === 'VẮNG MẶT') return 'text-rose-700 bg-rose-100 border-rose-200'
  if (status === 'DƯ CÔNG') return 'text-purple-700 bg-purple-100 border-purple-200'
  if (status === 'NGHỈ') return 'text-slate-500 bg-slate-100 border-slate-200'
  return 'text-slate-600 bg-slate-100'
}

// Convert DB ENUM to readable mapped text
const mapStatus = (status: string | null) => {
  if (!status) return 'Trống'
  return ATTENDANCE_STATUS_LABELS[status] || status
}

// Color badge class theo loại trạng thái (Tinh tế hơn, không border/shadow)
const getStatusBadgeClass = (status: string | null) => {
  if (!status || status === '' || status === 'NONE') return 'text-slate-400 bg-slate-100/50'
  if (status === 'FULLTIME' || status === 'FULL') return 'text-emerald-600 bg-emerald-50'
  if (['MISSING_TIME', 'MISSING', 'MORNING_MISSING', 'AFTERNOON_MISSING'].includes(status)) return 'text-amber-600 bg-amber-50'
  if (['MORNING_MISSING_PAID', 'AFTERNOON_MISSING_PAID'].includes(status)) return 'text-orange-600 bg-orange-50'
  if (['PAID_LEAVE', 'MORNING_PAID_LEAVE', 'AFTERNOON_PAID_LEAVE'].includes(status)) return 'text-sky-600 bg-sky-50'
  if (['ABSENT', 'MORNING_ABSENT', 'AFTERNOON_ABSENT'].includes(status)) return 'text-rose-600 bg-rose-50'
  if (status === 'OT_APPROVED' || status === 'OT') return 'text-purple-600 bg-purple-50'
  return 'text-slate-600 bg-slate-100'
}

const getStatusDotClass = (status: string | null) => {
  if (!status || status === '' || status === 'NONE') return 'bg-slate-300'
  if (status === 'FULLTIME' || status === 'FULL') return 'bg-emerald-500'
  if (['MISSING_TIME', 'MISSING', 'MORNING_MISSING', 'AFTERNOON_MISSING'].includes(status)) return 'bg-amber-500'
  if (['MORNING_MISSING_PAID', 'AFTERNOON_MISSING_PAID'].includes(status)) return 'bg-orange-500'
  if (['PAID_LEAVE', 'MORNING_PAID_LEAVE', 'AFTERNOON_PAID_LEAVE'].includes(status)) return 'bg-sky-500'
  if (['ABSENT', 'MORNING_ABSENT', 'AFTERNOON_ABSENT'].includes(status)) return 'bg-rose-500'
  if (status === 'OT_APPROVED' || status === 'OT') return 'bg-purple-500'
  return 'bg-slate-400'
}

// Map Modal Logic
const isMapVisible = ref(false)
const selectedLocation = ref({ lat: 0, lng: 0, title: '' })

const showMap = (log: any) => {
  selectedLocation.value = {
    lat: log.lat,
    lng: log.lng,
    title: log.location
  }
  isMapVisible.value = true
}

// Image Modal Logic
const isImageVisible = ref(false)
const selectedImage = ref('')

const showImage = (path: string) => {
  selectedImage.value = path
  isImageVisible.value = true
}

// Mock Data for Demo (User requested more requests)
const demoRequests = [
  {
    id: 101,
    title: "Bổ sung công (Quên quẹt thẻ)",
    status: "Đã duyệt",
    requester_name: "Kevin (Bạn)",
    at: "2026-03-23 08:35:00",
    reason: "Sáng nay đi vội quá nên quên quẹt thẻ lúc vào cửa, đã có check-in bằng app nhưng chưa có log vân tay.",
    approver_name: "Nguyễn Văn Trưởng Phòng",
    approved_at: "2026-03-23 09:15:00",
    approved_reason: "Đã kiểm tra log camera và app, xác nhận có mặt đúng giờ. Đồng ý bổ sung công."
  },
  {
    id: 102,
    title: "Đăng ký làm thêm giờ (OT)",
    status: "Đang chờ",
    requester_name: "Kevin (Bạn)",
    at: "2026-03-23 17:00:00",
    reason: "Tăng ca hoàn thiện Module Attendance UI theo yêu cầu gấp từ khách hàng (OneNetwork Project).",
    approver_name: "---",
    approved_at: "---",
    approved_reason: "Vui lòng chờ quản lý trực tiếp xem xét và phê duyệt sau khi kiểm tra log task."
  },
  {
    id: 103,
    title: "Nghỉ phép năm (Phép hưởng lương)",
    status: "Đã duyệt",
    requester_name: "Kevin (Bạn)",
    at: "2026-03-10 14:00:00",
    reason: "Giải quyết việc gia đình đột xuất tại quê nhà.",
    approver_name: "HR Department",
    approved_at: "2026-03-11 08:00:00",
    approved_reason: "Đã trừ 1 ngày vào quỹ phép năm 2026. Chúc bạn giải quyết việc gia đình thuận lợi."
  }
]

const displayRequests = computed(() => {
  const apiReqs = dayDetail.value?.requests || []
  // Nếu chưa có data từ API, show toàn bộ Demo. Nếu có rồi thì gộp chung (Demo lên đầu để dễ xem)
  return [...demoRequests, ...apiReqs]
})
</script>

<template>
  <Teleport to="body">
    <div v-if="isVisible" class="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm transition-opacity p-4">
      <div class="bg-slate-50 rounded-2xl shadow-2xl w-[95vw] max-w-[80vw] overflow-hidden flex flex-col max-h-[95vh] border border-slate-200/50">
        <!-- Header -->
        <div class="px-6 py-4 border-b border-slate-200 flex justify-between items-center bg-white shrink-0 rounded-lg relative z-10">
          <h3 class="text-xl font-bold text-slate-800 flex items-center gap-2">
            <span class="text-sky-500">📅</span>
            Quản lý ca làm việc Ngày {{ selectedDay < 10 ? '0' + selectedDay : selectedDay }}/{{ month < 10 ? '0' + month : month }}/{{ year }}
          </h3>
          <button @click="closeModal" class="text-slate-400 hover:text-red-500 transition-colors bg-slate-100 hover:bg-red-50 rounded-full w-8 h-8 flex items-center justify-center cursor-pointer">
            <span class="text-xl font-medium leading-none">&times;</span>
          </button>
        </div>

        <!-- Body -->
        <div class="overflow-y-auto flex-1 p-6" v-if="selectedEmp && selectedDay">
          <div class="grid grid-cols-1 lg:grid-cols-12 gap-8">

            <!-- LEFT COLUMN: Employee Card Only (Span 3) -->
            <div class="lg:col-span-3 space-y-6">
              <!-- 1. CÁ NHÂN -->
              <div class="bg-white rounded-lg rounded-lg border border-slate-200 overflow-hidden sticky top-0">
                <div class="bg-gradient-to-br from-sky-500 via-blue-500 to-indigo-600 h-20 opacity-90 relative overflow-hidden">
                  <div class="absolute -right-4 -bottom-4 w-24 h-24 bg-white/10 rounded-full blur-xl"></div>
                  <div class="absolute -left-4 -top-4 w-16 h-16 bg-white/20 rounded-full blur-lg"></div>
                </div>
                <div class="px-5 pb-6 relative">
                  <div class="w-20 h-20 bg-white rounded-full border-4 border-white shadow-md absolute -top-10 left-1/2 -translate-x-1/2 flex items-center justify-center text-3xl font-bold text-sky-600 overflow-hidden shrink-0">
                    <img :src="`https://api.dicebear.com/7.x/avataaars/svg?seed=${selectedEmp.code}`" alt="Avatar" class="w-full h-full object-cover rounded-full" />
                  </div>
                  <div class="pt-12 text-center">
                    <h4 class="text-lg font-bold text-slate-800 tracking-tight">{{ selectedEmp.name }}</h4>
                    <p class="text-[13px] font-medium text-slate-500 mt-1">{{ selectedEmp.code }} • {{ selectedEmp.department }}</p>

                    <!-- NEW: Overall Status Badge -->
                    <div v-if="dayDetail?.stats?.overall_status" class="mt-4">
                      <div class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black border uppercase tracking-widest shadow-sm" :class="getOverallStatusClass(dayDetail.stats.overall_status)">
                        {{ dayDetail.stats.overall_status }}
                      </div>
                    </div>

                    <div class="mt-6 border-t border-slate-100 pt-4 cursor-default">
                      <div class="flex justify-between items-center bg-slate-50 px-3 py-2 rounded mb-2">
                        <span class="text-xs text-slate-500 uppercase font-semibold">Công chuẩn</span>
                        <span class="font-bold text-slate-700">{{ selectedEmp.summary?.work || 0 }}</span>
                      </div>
                      <div class="flex justify-between items-center bg-slate-50 px-3 py-2 rounded">
                        <span class="text-xs text-slate-500 uppercase font-semibold">Tăng ca (OT)</span>
                        <span class="font-bold text-slate-700">{{ (selectedEmp.summary?.ot15 || 0) + (selectedEmp.summary?.ot20 || 0) }}h</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- RIGHT COLUMN: Data Tracker & HR Action (Span 9) -->
            <div class="lg:col-span-9 flex flex-col gap-6">
              <!-- NEW: Overall Day Stats -->
              <div class="bg-gradient-to-r from-sky-50 to-blue-50/50 rounded-lg p-4 border border-sky-100 shadow-sm flex items-center justify-between">
                <div class="flex items-center gap-3">
                  <span class="text-3xl leading-none">✨</span>
                  <div>
                    <p class="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Tổng kết Năng suất Ngày:</p>
                    <p class="font-black text-sky-600 text-xl leading-tight">{{ dayDetail?.stats?.total_hours || 0 }}h <span class="text-sm text-slate-400 font-bold ml-1">{{ dayDetail?.stats?.work_day || 0 }} công</span></p>
                  </div>
                </div>
                <div class="flex flex-wrap gap-2 align-middle justify-end">
                  <div v-if="dayDetail?.stats?.overall_status" class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black border uppercase tracking-widest shadow-sm" :class="getOverallStatusClass(dayDetail.stats.overall_status)">
                    {{ dayDetail.stats.overall_status }}
                  </div>
                </div>
              </div>
              <!-- ROW 1: THÔNG SỐ CHUNG -->
              <div class="bg-white rounded-lg rounded-lg border border-slate-200 overflow-hidden">
                <div class="bg-slate-50 border-b border-slate-100 px-5 py-3">
                  <h4 class="font-bold text-slate-800 flex items-center gap-2 text-[13px] uppercase tracking-wide">
                    <span class="text-indigo-500 text-lg leading-none">📊</span> Thông số Hệ thống chấm công
                  </h4>
                </div>
                <div class="p-0 overflow-x-auto">
                  <table class="w-full text-left border-collapse min-w-[500px]">
                    <thead>
                      <tr class="bg-white text-[10px] text-slate-400 uppercase tracking-widest border-b border-slate-100">
                        <th class="py-2.5 px-5 font-extrabold whitespace-nowrap">Ca làm việc</th>
                        <th class="py-2.5 px-5 font-extrabold whitespace-nowrap">Giờ Quy định</th>
                        <th class="py-2.5 px-5 font-extrabold whitespace-nowrap">Check-In Vào</th>
                        <th class="py-2.5 px-5 font-extrabold whitespace-nowrap">Check-Out Ra</th>
                        <th class="py-2.5 px-5 font-extrabold text-center whitespace-nowrap">Năng suất</th>
                        <th class="py-2.5 px-5 font-extrabold whitespace-nowrap">Trạng thái buổi</th>
                      </tr>
                    </thead>
                    <tbody class="text-[13px]">
                      <!-- Sáng -->
                      <tr class="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                        <td class="py-3 px-5 font-bold text-slate-700 flex items-center gap-2"><span class="w-2 h-2 rounded-full bg-blue-500"></span> Buổi Sáng</td>
                        <td class="py-3 px-5 text-slate-400 italic text-[11px]">{{ dayDetail?.rules?.morning?.start || '08:00' }} - {{ dayDetail?.rules?.morning?.end || '12:00' }}</td>
                        <td class="py-3 px-5 text-slate-600 font-mono" :class="{ 'text-rose-500 font-bold': dayDetail?.stats?.morning_start > '08:15' }">
                          {{ getShiftDetail('morning').in }}
                        </td>
                        <td class="py-3 px-5 text-slate-600 font-mono" :class="{ 'text-rose-500 font-bold': dayDetail?.stats?.morning_end && dayDetail?.stats?.morning_end < '12:00' }">
                          {{ getShiftDetail('morning').out }}
                        </td>
                        <td class="py-3 px-5 font-bold text-center text-slate-800">{{ dayDetail?.stats?.morning_hours || 0 }}h</td>
                        <td class="py-3 px-5">
                          <div :class="getStatusBadgeClass(dayDetail?.stats?.am)" class="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider">
                            <span class="w-1.5 h-1.5 rounded-full" :class="getStatusDotClass(dayDetail?.stats?.am)"></span>
                            {{ mapStatus(dayDetail?.stats?.am) }}
                          </div>
                        </td>
                      </tr>
                      <!-- Chiều -->
                      <tr class="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                        <td class="py-3 px-5 font-bold text-slate-700 flex items-center gap-2"><span class="w-2 h-2 rounded-full bg-orange-500"></span> Buổi Chiều</td>
                        <td class="py-3 px-5 text-slate-400 italic text-[11px]">{{ dayDetail?.rules?.afternoon?.start || '13:30' }} - {{ dayDetail?.rules?.afternoon?.end || '17:30' }}</td>
                        <td class="py-3 px-5 text-slate-600 font-mono" :class="{ 'text-rose-500 font-bold': dayDetail?.stats?.afternoon_start > '13:45' }">
                          {{ getShiftDetail('afternoon').in }}
                        </td>
                        <td class="py-3 px-5 text-slate-600 font-mono" :class="{ 'text-rose-500 font-bold': dayDetail?.stats?.afternoon_end && dayDetail?.stats?.afternoon_end < '17:30' }">
                          {{ getShiftDetail('afternoon').out }}
                        </td>
                        <td class="py-3 px-5 font-bold text-center text-slate-800">{{ dayDetail?.stats?.afternoon_hours || 0 }}h</td>
                        <td class="py-3 px-5">
                          <div :class="getStatusBadgeClass(dayDetail?.stats?.pm)" class="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider">
                            <span class="w-1.5 h-1.5 rounded-full" :class="getStatusDotClass(dayDetail?.stats?.pm)"></span>
                            {{ mapStatus(dayDetail?.stats?.pm) }}
                          </div>
                        </td>
                      </tr>
                      <!-- OT -->
                      <tr class="border-b border-slate-100 hover:bg-slate-50/50 transition-colors">
                        <td class="py-3 px-5 font-bold text-slate-700 flex items-center gap-2"><span class="w-2 h-2 rounded-full bg-purple-500"></span> Quá giờ (OT)</td>
                        <td class="py-3 px-5 text-slate-400 italic text-[11px]">{{ dayDetail?.rules?.ot?.start || '--' }} - {{ dayDetail?.rules?.ot?.end || '--' }}</td>
                        <td class="py-3 px-5 text-slate-600 font-mono">{{ getShiftDetail('ot').in }}</td>
                        <td class="py-3 px-5 text-slate-600 font-mono">{{ getShiftDetail('ot').out }}</td>
                        <td class="py-3 px-5 font-bold text-center text-purple-600">
                          {{ dayDetail?.stats?.extra_hours || 0 }}h
                          <div v-if="dayDetail?.stats?.ot_factor" class="text-[9px] text-purple-400 font-black">Hệ số x{{ dayDetail.stats.ot_factor / 10 }}</div>
                        </td>
                        <td class="py-3 px-5">
                          <div v-if="dayDetail?.stats?.extra_hours > 0 && dayDetail?.stats?.ot_factor" class="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-purple-50 text-purple-600 text-[10px] font-bold uppercase tracking-wider">
                            <span class="w-1.5 h-1.5 rounded-full bg-purple-500"></span>
                            Tăng ca
                          </div>
                          <span v-else class="text-[11px] text-slate-300 italic">--</span>
                        </td>
                      </tr>
                      <!-- Tổng ngày -->

                    </tbody>
                  </table>
                </div>
              </div>

              <!-- ROW 2: LOG TIMELINE & TICKETS (Grid 2 Cols) -->
              <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <!-- LOG CHECK-IN -->
                <div class="bg-white rounded-lg rounded-lg border border-slate-200 flex flex-col h-full max-h-[350px]">
                  <div class="bg-slate-50 border-b border-slate-100 px-5 py-3 shrink-0">
                    <h4 class="font-bold text-slate-800 flex items-center gap-2 text-[13px] uppercase tracking-wide">
                      <span class="text-teal-500 text-lg leading-none">📍</span> Lịch sử ra vào
                    </h4>
                  </div>
                  <div class="p-4 overflow-y-auto flex-1 custom-scrollbar">
                    <div class="relative border-l border-slate-100 ml-2 space-y-3">
                      <div v-for="(log, idx) in dayDetail?.logs || []" :key="idx" class="relative pl-5">
                        <div class="absolute -left-[5px] top-1.5 w-2.5 h-2.5 rounded-full border-2 border-white" :class="log.type === 'IN' ? 'bg-teal-500' : 'bg-rose-500'"></div>
                        <div class="bg-white border border-slate-100 rounded-lg p-2 hover:border-slate-200 hover:shadow-sm transition-all group/log">
                          <div class="flex items-center gap-2.5">
                            <img
                              v-if="log.image?.url?.thumbnail"
                              :src="log.image?.url?.thumbnail"
                              class="w-8 h-8 rounded-md object-cover border border-slate-100 shrink-0 cursor-pointer hover:ring-2 hover:ring-sky-400 transition-all"
                              alt="Checkin"
                              @click="showImage(log.image?.url?.original)"
                            >
                            <div v-else class="w-8 h-8 rounded-md bg-slate-50 border border-slate-100 flex items-center justify-center shrink-0">
                              <span class="text-[10px] opacity-30">📷</span>
                            </div>

                            <div class="flex-1 flex items-center gap-3 min-w-0">
                              <span class="text-[12px] font-bold text-slate-800 font-mono shrink-0">{{ log.time }}</span>

                              <p class="text-[11px] text-slate-600 truncate flex-1 min-w-0">
                                {{ log.location }}
                              </p>

                              <button
                                @click="showMap(log)"
                                class="flex items-center gap-1 px-1.5 py-0.5 rounded bg-sky-50 text-sky-600 hover:bg-sky-100 transition-colors shrink-0 cursor-pointer"
                              >
                                <span class="text-[10px] leading-none">📍</span>
                                <span class="text-[9px] font-bold font-mono tracking-tighter">{{ log.lat }},{{ log.lng }}</span>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div v-if="(!dayDetail?.logs) || dayDetail.logs.length === 0" class="pl-6 text-sm text-slate-400 italic">
                        Chưa có lịch sử quẹt thẻ/cảm biến trong ngày.
                      </div>
                    </div>
                  </div>
                </div>

                <!-- REQUESTS / ĐƠN TỪ -->
                <div class="bg-white rounded-lg rounded-lg border border-slate-200 flex flex-col h-full max-h-[350px]">
                  <div class="bg-slate-50 border-b border-slate-100 px-5 py-3 flex items-center justify-between shrink-0">
                    <h4 class="font-bold text-slate-800 flex items-center gap-2 text-[13px] uppercase tracking-wide">
                      <span class="text-amber-500 text-lg leading-none">📝</span> Đơn từ / Yêu cầu Xét duyệt
                      <span class="bg-amber-100 text-amber-700 text-[10px] font-extrabold px-2 py-0.5 rounded-full">{{ (dayDetail?.requests || []).length || 0 }}</span>
                    </h4>
                    <button class="text-[10px] bg-sky-50 text-sky-600 hover:bg-sky-500 hover:text-white border border-sky-100 hover:border-sky-500 font-bold px-2 py-1 rounded transition-colors flex items-center gap-1 uppercase tracking-wider cursor-pointer">
                      Khởi tạo Ticket
                    </button>
                  </div>

                  <div class="p-4 overflow-y-auto flex-1 custom-scrollbar space-y-2 bg-slate-50/20">
                    <div v-for="req in displayRequests" :key="req.id" class="bg-white border border-slate-100 rounded-lg p-2.5 hover:border-slate-200 hover:shadow-sm transition-all group/req">
                      <!-- Header: Title & Status -->
                      <div class="flex items-center justify-between gap-3 mb-1.5 pb-1.5 border-b border-slate-50">
                        <div class="flex items-center gap-1.5 truncate">
                          <span class="text-[11px] font-black text-slate-800 uppercase tracking-tight truncate">{{ req.title }}</span>
                        </div>
                        <span class="text-[8px] font-black px-1.5 py-0.5 rounded border uppercase shrink-0" :class="req.status === 'Đã duyệt' ? 'bg-teal-50 text-teal-600 border-teal-100' : 'bg-amber-50 text-amber-600 border-amber-100'">
                          {{ req.status }}
                        </span>
                      </div>

                      <!-- Compact Content Grid -->
                      <div class="space-y-2">
                        <!-- Request Part -->
                        <div class="flex flex-col gap-1">
                          <div class="flex items-center justify-between text-[10px]">
                            <span class="text-sky-600 font-bold">📤 {{ req.requester_name || selectedEmp?.name || '---' }}</span>
                            <span class="text-slate-400 font-mono">{{ req.at || req.time || '---' }}</span>
                          </div>
                          <p class="text-[10px] text-slate-500 italic bg-sky-50/30 px-2 py-1 rounded border-l border-sky-200/50">"{{ req.reason }}"</p>
                        </div>

                        <!-- Approval Part (If exists) -->
                        <div v-if="req.approver_name || req.approver" class="flex flex-col gap-1 border-t border-slate-50 pt-2">
                          <div class="flex items-center justify-between text-[10px]">
                            <span class="text-teal-600 font-bold">📥 {{ req.approver_name || req.approver || '---' }}</span>
                            <span class="text-slate-400 font-mono">{{ req.approved_at || '---' }}</span>
                          </div>
                          <p class="text-[10px] text-slate-500 bg-teal-50/30 px-2 py-1 rounded border-l border-teal-200/50">
                            <span class="text-teal-500 mr-1">{{ req.status === 'Đã duyệt' ? '✅' : '⏳' }}</span>
                            {{ req.approved_reason || 'Đang chờ xem xét...' }}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div v-if="displayRequests.length === 0" class="text-center py-12 text-xs text-slate-400 border border-dashed border-slate-200 rounded-2xl bg-white/50">
                      <div class="text-3xl mb-3 opacity-20 filter grayscale">📋</div>
                      Nhân sự không nộp đơn từ nào trong ngày này.
                    </div>
                  </div>
                </div>
              </div>

              <!-- ROW 3: HIỆU CHỈNH HR -->
              <div class="bg-white rounded-lg shadow-[0_4px_20px_-5px_rgba(225,29,72,0.15)] border-2 border-rose-100 relative overflow-hidden group">
                <div class="absolute right-0 top-0 w-32 h-32 bg-rose-500 opacity-[0.03] rounded-bl-full pointer-events-none group-hover:scale-110 transition-transform duration-500"></div>

                <div class="bg-rose-50/50 border-b border-rose-100/50 px-5 py-3 flex items-center justify-between">
                  <h4 class="font-bold text-slate-800 flex items-center gap-2 text-[13px] uppercase tracking-wide">
                    <span class="text-rose-500 text-lg leading-none">⚙️</span> Điều chỉnh bởi HR
                  </h4>
                  <span class="text-[9px] bg-rose-100 text-rose-700 px-2.5 py-1 rounded rounded-lg border border-rose-200 font-extrabold uppercase tracking-widest">Ghi đè - Final Record</span>
                </div>

                <div class="p-5">
                  <div class="flex flex-col md:flex-row gap-6">
                    <!-- Trạng thái -->
                    <div class="flex-1 space-y-4 md:border-r border-slate-100 md:pr-6">
                      <div class="flex items-center gap-3">
                        <label class="w-20 text-[10px] font-extrabold text-blue-600 uppercase tracking-wider shrink-0">Ca Sáng:</label>
                        <select v-model="tempAmStatus" :disabled="selectedEmp.attendance[selectedDay].am === null" class="flex-1 appearance-none bg-slate-50 border border-slate-200 text-slate-700 py-1.5 px-3 rounded rounded-lg text-xs font-bold focus:outline-none focus:ring-2 focus:ring-rose-200 focus:bg-white transition-all cursor-pointer">
                          <option v-for="opt in HR_EDIT_STATUS_OPTIONS" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                        </select>
                      </div>
                      <div class="flex items-center gap-3">
                        <label class="w-20 text-[10px] font-extrabold text-orange-600 uppercase tracking-wider shrink-0">Ca Chiều:</label>
                        <select v-model="tempPmStatus" :disabled="selectedEmp.attendance[selectedDay].pm === null" class="flex-1 appearance-none bg-slate-50 border border-slate-200 text-slate-700 py-1.5 px-3 rounded rounded-lg text-xs font-bold focus:outline-none focus:ring-2 focus:ring-rose-200 focus:bg-white transition-all cursor-pointer">
                          <option v-for="opt in HR_EDIT_STATUS_OPTIONS" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                        </select>
                      </div>
                    </div>

                    <!-- Thông số -->
                    <div class="flex-1 space-y-4">
                      <div class="flex items-center gap-3">
                        <label class="w-24 text-[10px] font-extrabold text-slate-500 uppercase tracking-wider shrink-0">Công (Days):</label>
                        <input type="number" step="0.25" min="0" max="1" v-model="tempWorkDays" class="flex-1 bg-slate-50 border border-slate-200 text-slate-700 py-1.5 px-3 rounded rounded-lg text-sm font-bold focus:outline-none focus:ring-2 focus:ring-rose-200 focus:bg-white transition-all">
                      </div>
                      <div class="flex items-center gap-3">
                        <label class="w-24 text-[10px] font-extrabold text-slate-500 uppercase tracking-wider shrink-0">Giờ (Hours):</label>
                        <input type="number" step="1" min="0" max="24" v-model="tempWorkHours" class="flex-1 bg-slate-50 border border-slate-200 text-slate-700 py-1.5 px-3 rounded rounded-lg text-sm font-bold focus:outline-none focus:ring-2 focus:ring-rose-200 focus:bg-white transition-all">
                      </div>
                    </div>
                  </div>
                  <div class="flex flex-col md:flex-row gap-6 mt-3">
                    <!-- Lý do & Lưu -->
                    <div class="flex-1 flex flex-col justify-between">
                      <textarea v-model="tempNote" rows="2" class="w-full bg-slate-50 border border-slate-200 text-slate-700 py-2 px-3 rounded rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-rose-200 focus:bg-white transition-all resize-none placeholder:text-slate-400 mb-3 block" placeholder="Nhập lý do chốt công (VD: Duyệt bù thiếu giờ sáng do quên thẻ...)"></textarea>
                      <p class="text-[10px] text-slate-400 italic font-medium leading-tight mb-0">Thay đổi sẽ được lưu lịch sử tự động.</p>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="px-6 py-4 bg-white border-t border-slate-200 flex justify-end gap-3 shrink-0">
          <button @click="closeModal" class="px-5 py-2 rounded-lg font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 transition-colors text-sm">Đóng lại (Cancel)</button>
          <button @click="saveAttendance" :disabled="!tempNote" class="px-5 py-2 rounded-lg font-bold text-white bg-sky-500 hover:bg-sky-600 transition-colors rounded-lg flex items-center gap-2 disabled:bg-slate-300 text-sm">
            <span v-if="!tempNote" class="text-base text-slate-100/50">⚠️ Nhập lý do trước</span>
            Chốt Công Ngay (Apply Override)
          </button>
        </div>
      </div>
    </div>
  </Teleport>

  <!-- MAP POPUP OVERLAY -->
  <Teleport to="body">
    <div v-if="isMapVisible" class="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4">
      <div class="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden border border-slate-100">
        <div class="bg-slate-50 border-b border-slate-100 px-5 py-4 flex items-center justify-between">
          <div class="flex items-center gap-3">
            <span class="text-xl leading-none">📍</span>
            <div>
              <h3 class="font-bold text-slate-800 text-[15px] leading-tight">Vị trí Check-in</h3>
              <p class="text-[11px] text-slate-500 truncate max-w-[280px]">{{ selectedLocation.title }}</p>
            </div>
          </div>
          <button @click="isMapVisible = false" class="text-slate-400 hover:text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-full w-8 h-8 flex items-center justify-center transition-all cursor-pointer">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M6 18L18 6M6 6l12 12"></path></svg>
          </button>
        </div>

        <div class="relative bg-slate-50 h-[350px]">
          <iframe
            width="100%"
            height="100%"
            frameborder="0"
            style="border:0;"
            allowfullscreen
            loading="lazy"
            referrerpolicy="no-referrer-when-downgrade"
            :src="`https://maps.google.com/maps?q=${selectedLocation.lat},${selectedLocation.lng}&t=&z=16&ie=UTF8&iwloc=&output=embed`"
          ></iframe>
        </div>

        <div class="bg-white px-5 py-4 border-t border-slate-100 flex items-center justify-between">
          <div class="bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-100 font-mono text-[11px] font-bold text-slate-600">
            {{ selectedLocation.lat }}, {{ selectedLocation.lng }}
          </div>
          <button @click="isMapVisible = false" class="bg-slate-800 text-white font-bold px-6 py-2 rounded-lg text-[13px] hover:bg-slate-950 transition-all shadow-lg active:scale-95 cursor-pointer">
            Đóng
          </button>
        </div>
      </div>
    </div>
  </Teleport>
  <!-- IMAGE PREVIEW OVERLAY -->
  <Teleport to="body">
    <div v-if="isImageVisible" @click="isImageVisible = false" class="fixed inset-0 z-[10001] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
      <div class="relative max-w-[90vw] max-h-[90vh] bg-white rounded-2xl p-1 shadow-2xl overflow-hidden border border-white/20 animate-in zoom-in duration-200" @click.stop>
        <img :src="selectedImage" class="max-w-full max-h-[80vh] rounded-xl object-contain shadow-lg" alt="Full Preview">

        <div class="absolute top-4 right-4 flex gap-2">
          <button @click="isImageVisible = false" class="bg-black/20 hover:bg-black/40 text-white backdrop-blur-md rounded-full w-10 h-10 flex items-center justify-center transition-all cursor-pointer border border-white/20">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M6 18L18 6M6 6l12 12"></path></svg>
          </button>
        </div>

        <div class="px-6 py-4 bg-white/90 backdrop-blur-md flex items-center justify-between">
          <div class="flex items-center gap-3">
            <span class="p-2 bg-sky-50 text-sky-500 rounded-lg">📸</span>
            <span class="text-sm font-bold text-slate-800">Ảnh Check-in thực tế</span>
          </div>
          <button @click="isImageVisible = false" class="text-sky-600 font-bold text-sm hover:underline cursor-pointer">Đóng cửa sổ</button>
        </div>
      </div>
    </div>
  </Teleport>
</template>
